import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";

// @mui material components
import Icon from "@mui/material/Icon";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

function RotatingCardFront({ color, image, icon, title, description }) {
    const [isInView, setIsInView] = useState(false);
    const cardRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsInView(true);
                    observer.disconnect(); // Stop observing once it's in view
                }
            },
            {
                threshold: 0.1, // Trigger when 10% of the element is in view
            }
        );

        if (cardRef.current) {
            observer.observe(cardRef.current);
        }

        return () => {
            if (observer && cardRef.current) {
                observer.disconnect();
            }
        };
    }, []);

    return (
        <MKBox
            ref={cardRef}
            display="flex"
            justifyContent="center"
            alignContent="center"
            borderRadius="lg"
            coloredShadow={color}
            width="100%"
            position="relative"
            zIndex={2}
            sx={{
                backgroundImage: isInView
                    ? ({ palette: { gradients }, functions: { linearGradient, rgba } }) =>
                          `${linearGradient(
                              rgba(
                                  gradients[color] ? gradients[color].main : gradients.info.main,
                                  0.85
                              ),
                              rgba(
                                  gradients[color] ? gradients[color].main : gradients.info.main,
                                  0.85
                              )
                          )}, url(${image})`
                    : "none", // Set to none initially
                backgroundSize: "cover",
                backfaceVisibility: "hidden",
            }}
        >
            <MKBox py={12} px={3} textAlign="center" lineHeight={1}>
                {icon && (
                    <MKTypography variant="h2" color="white" my={2}>
                        {typeof icon === "string" ? <Icon>{icon}</Icon> : icon}
                    </MKTypography>
                )}
                <MKTypography variant="h3" color="white" gutterBottom>
                    {title}
                </MKTypography>
                <MKTypography variant="body2" color="white" opacity={0.8}>
                    {description}
                </MKTypography>
            </MKBox>
        </MKBox>
    );
}

// Setting default props for the RotatingCardFront
RotatingCardFront.defaultProps = {
    color: "info",
    icon: "",
};

// Typechecking props for the RotatingCardFront
RotatingCardFront.propTypes = {
    color: PropTypes.oneOf([
        "primary",
        "secondary",
        "info",
        "success",
        "warning",
        "error",
        "dark",
        "light",
    ]),
    image: PropTypes.string.isRequired,
    icon: PropTypes.node,
    title: PropTypes.node.isRequired,
    description: PropTypes.node.isRequired,
};

export default RotatingCardFront;
